import React, { useEffect, useState } from 'react'
import Box from '@mui/joy/Box/index.js'
import Card from '@mui/joy/Card/index.js'
import CardContent from '@mui/joy/CardContent/index.js'
import Typography from '@mui/joy/Typography/index.js'
import CircularProgress from '@mui/joy/CircularProgress/index.js'
import Grid from '@mui/joy/Grid/index.js'
import Button from '@mui/joy/Button/index.js'
import ButtonGroup from '@mui/joy/ButtonGroup/index.js'
import List from '@mui/joy/List/index.js'
import ListItem from '@mui/joy/ListItem/index.js'
import ListItemContent from '@mui/joy/ListItemContent/index.js'
import Divider from '@mui/joy/Divider/index.js'
import Sheet from '@mui/joy/Sheet/index.js'
import { get } from 'aws-amplify/api'
import { fetchAuthSession } from 'aws-amplify/auth'
import dayjs from 'dayjs'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

export default function Statistics() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [timespan, setTimespan] = useState('all') // 'all', 'week', 'month', 'quarter', 'year'

  const fetchStats = async (timespanFilter) => {
    setLoading(true)
    setError(null)

    try {
      const s = await fetchAuthSession()
      const idToken = s.tokens?.idToken?.toString()

      // Calculate timespan boundaries
      const now = dayjs()

      const timespanMap = {
        week: { days: 7 },
        month: { days: 30 },
        quarter: { days: 90 },
        year: { days: 365 },
      }

      const timespanConfig = timespanMap[timespanFilter]
      const startDate =
        timespanConfig ?
          now.subtract(timespanConfig.days, 'day').valueOf()
        : null
      const endDate = timespanConfig ? now.valueOf() : null

      const queryParams = {
        ...(startDate && { startDate: startDate.toString() }),
        ...(endDate && { endDate: endDate.toString() }),
      }

      const restOperation = get({
        apiName: 'technician-bot',
        path: '/1/admin/stats',
        options: {
          headers: {
            Authorization: idToken,
          },
          queryParams,
        },
      })

      const { body } = await restOperation.response
      const response = await body.json()

      setStats(response)
    } catch (err) {
      console.error('Error fetching stats:', err)
      setError(err.message || 'Failed to load statistics')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchStats(timespan)
  }, [timespan])

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Card color="danger" variant="soft">
          <CardContent>
            <Typography level="title-md">Error Loading Statistics</Typography>
            <Typography level="body-sm">{error}</Typography>
            <Button
              variant="soft"
              color="danger"
              onClick={() => {
                void fetchStats(timespan)
              }}
              sx={{ mt: 2 }}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      </Box>
    )
  }

  if (!stats) {
    return null
  }

  const topUsers = stats.usersWithMultipleConversations.slice(0, 10)

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography level="h3">Usage Statistics</Typography>
        <ButtonGroup>
          <Button
            variant={timespan === 'week' ? 'solid' : 'outlined'}
            onClick={() => {
              setTimespan('week')
            }}
          >
            Last 7 Days
          </Button>
          <Button
            variant={timespan === 'month' ? 'solid' : 'outlined'}
            onClick={() => {
              setTimespan('month')
            }}
          >
            Last 30 Days
          </Button>
          <Button
            variant={timespan === 'quarter' ? 'solid' : 'outlined'}
            onClick={() => {
              setTimespan('quarter')
            }}
          >
            Last 90 Days
          </Button>
          <Button
            variant={timespan === 'year' ? 'solid' : 'outlined'}
            onClick={() => {
              setTimespan('year')
            }}
          >
            Last Year
          </Button>
          <Button
            variant={timespan === 'all' ? 'solid' : 'outlined'}
            onClick={() => {
              setTimespan('all')
            }}
          >
            All Time
          </Button>
        </ButtonGroup>
      </Box>

      <Grid container spacing={3}>
        <Grid xs={12} sm={6} md={3}>
          <Card variant="soft" color="primary">
            <CardContent>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Total Conversations
              </Typography>
              <Typography level="h2">
                {stats.totalConversations.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <Card variant="soft" color="success">
            <CardContent>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Total Users
              </Typography>
              <Typography level="h2">
                {stats.totalUsers.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <Card variant="soft" color="warning">
            <CardContent>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Conversations per User
              </Typography>
              <Typography level="h2">
                {stats.averageConversationsPerUser.toFixed(2)}
              </Typography>
              <Typography level="body-xs" sx={{ mt: 0.5 }}>
                Median: {stats.medianConversationsPerUser.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <Card variant="soft" color="neutral">
            <CardContent>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Active Users
              </Typography>
              <Typography level="h2">
                {Object.keys(stats.conversationsByUser).length.toLocaleString()}
              </Typography>
              <Typography level="body-xs" sx={{ mt: 0.5 }}>
                {(
                  (Object.keys(stats.conversationsByUser).length /
                    stats.totalUsers) *
                  100
                ).toFixed(1)}
                % of total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Message Statistics Row */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid xs={12} sm={6} md={3}>
          <Card variant="soft" color="primary">
            <CardContent>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Total Messages
              </Typography>
              <Typography level="h2">
                {stats.totalMessages.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <Card variant="soft" color="success">
            <CardContent>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Messages per Conversation
              </Typography>
              <Typography level="h2">
                {stats.averageMessagesPerConversation.toFixed(1)}
              </Typography>
              <Typography level="body-xs" sx={{ mt: 0.5 }}>
                Median: {stats.medianMessagesPerConversation.toFixed(1)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <Card variant="soft" color="warning">
            <CardContent>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Longest Conversation
              </Typography>
              <Typography level="h2">
                {stats.longestConversationMessages}
              </Typography>
              <Typography level="body-xs" sx={{ mt: 0.5 }}>
                messages
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <Card variant="soft" color="neutral">
            <CardContent>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Multi-Turn Users
              </Typography>
              <Typography level="h2">
                {stats.usersWithMultiTurnConversations.toLocaleString()}
              </Typography>
              <Typography level="body-xs" sx={{ mt: 0.5 }}>
                {stats.totalUsers > 0 ?
                  (
                    (stats.usersWithMultiTurnConversations / stats.totalUsers) *
                    100
                  ).toFixed(1)
                : 0}
                % of total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Line Graph: Conversations Over Time */}
      {stats.conversationsPerDay && stats.conversationsPerDay.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Conversations Over Time
            </Typography>
            <Typography level="body-sm" sx={{ mb: 3, color: 'text.secondary' }}>
              Daily conversation count from{' '}
              {dayjs(stats.timespanStart).format('MMM D, YYYY')} to{' '}
              {dayjs(stats.timespanEnd).format('MMM D, YYYY')}
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={stats.conversationsPerDay}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => dayjs(date).format('MMM D')}
                />
                <YAxis />
                <Tooltip
                  labelFormatter={(date) => dayjs(date).format('MMM D, YYYY')}
                  formatter={(value) => [value, 'Conversations']}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#0B6BCB"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            Top Active Users
          </Typography>
          <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
            Users with the most conversations{' '}
            {stats.filteredByTimespan ? 'in selected timespan' : '(all time)'}
          </Typography>

          {topUsers.length === 0 ?
            <Typography
              level="body-sm"
              sx={{ fontStyle: 'italic', color: 'text.tertiary' }}
            >
              No users with multiple conversations in this timespan
            </Typography>
          : <List>
              {topUsers.map((user, index) => (
                <React.Fragment key={user.userId}>
                  <ListItem>
                    <ListItemContent>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                          }}
                        >
                          <Sheet
                            variant="soft"
                            color="primary"
                            sx={{
                              width: 32,
                              height: 32,
                              minWidth: 32,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                            }}
                          >
                            {index + 1}
                          </Sheet>
                          <Box sx={{ flex: 1 }}>
                            <Typography level="title-sm">
                              {user.userId}
                            </Typography>
                            <Typography
                              level="body-xs"
                              sx={{ color: 'text.tertiary', mt: 0.5 }}
                            >
                              {user.conversationCount} conversation
                              {user.conversationCount !== 1 ? 's' : ''}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 2,
                                mt: 1,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Typography level="body-xs">
                                <strong>Avg:</strong>{' '}
                                {user.averageMessagesPerConversation.toFixed(1)}{' '}
                                msg/conv
                              </Typography>
                              <Typography level="body-xs">
                                <strong>Median:</strong>{' '}
                                {user.medianMessagesPerConversation.toFixed(1)}{' '}
                                msg/conv
                              </Typography>
                              <Typography level="body-xs">
                                <strong>Longest:</strong>{' '}
                                {user.longestConversationMessages} messages
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Typography level="h4" sx={{ color: 'primary.500' }}>
                          {user.conversationCount}
                        </Typography>
                      </Box>
                    </ListItemContent>
                  </ListItem>
                  {index < topUsers.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          }

          {stats.usersWithMultipleConversations.length > 10 && (
            <Typography
              level="body-xs"
              sx={{ mt: 2, color: 'text.tertiary', fontStyle: 'italic' }}
            >
              Showing top 10 of {stats.usersWithMultipleConversations.length}{' '}
              users with multiple conversations
            </Typography>
          )}
        </CardContent>
      </Card>

      {stats.filteredByTimespan && (
        <Typography
          level="body-xs"
          sx={{ mt: 2, color: 'text.tertiary', fontStyle: 'italic' }}
        >
          Statistics filtered from{' '}
          {dayjs(stats.timespanStart).format('MMM D, YYYY')} to{' '}
          {dayjs(stats.timespanEnd).format('MMM D, YYYY')}
        </Typography>
      )}
    </Box>
  )
}
